/**
 * Sprint G real · #14 · PasskeysCard
 *
 * Permite al usuario registrar passkeys (Face ID / Touch ID / Windows Hello) y
 * gestionarlas (listar y revocar). El login con passkey se hace en LoginPage.
 */
import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Fingerprint, Trash, ShieldCheck, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

// Convierte base64url a Uint8Array
function b64urlToBytes(b64url) {
  const pad = "=".repeat((4 - (b64url.length % 4)) % 4);
  const b64 = (b64url + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf;
}

// Convierte ArrayBuffer/Uint8Array → base64url
function bytesToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function publicKeyOptsToBrowser(opts) {
  return {
    ...opts,
    challenge: b64urlToBytes(opts.challenge),
    user: opts.user ? { ...opts.user, id: b64urlToBytes(opts.user.id) } : undefined,
    excludeCredentials: (opts.excludeCredentials || []).map((c) => ({ ...c, id: b64urlToBytes(c.id) })),
    allowCredentials: (opts.allowCredentials || []).map((c) => ({ ...c, id: b64urlToBytes(c.id) })),
  };
}

function attestationToJson(cred) {
  return {
    id: cred.id,
    rawId: bytesToB64url(cred.rawId),
    type: cred.type,
    response: {
      attestationObject: bytesToB64url(cred.response.attestationObject),
      clientDataJSON: bytesToB64url(cred.response.clientDataJSON),
      transports: cred.response.getTransports ? cred.response.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults?.() || {},
  };
}

export function PasskeysCard() {
  const [creds, setCreds] = useState([]);
  const [deviceName, setDeviceName] = useState("");
  const [busy, setBusy] = useState(false);
  const supported = typeof window !== "undefined" && !!window.PublicKeyCredential;

  const load = async () => {
    try { const { data } = await api.get("/webauthn/credentials"); setCreds(data || []); }
    catch { setCreds([]); }
  };
  useEffect(() => { load(); }, []);

  const register = async () => {
    if (!supported) { toast.error("Tu navegador no soporta passkeys"); return; }
    if (!window.isSecureContext) {
      toast.error("Las passkeys requieren HTTPS válido. Cierra el navegador y vuelve a abrir el dominio.");
      return;
    }
    setBusy(true);
    try {
      const { data: opts } = await api.post("/webauthn/register/options", { device_name: deviceName });
      const browserOpts = publicKeyOptsToBrowser(opts);
      let credential;
      try {
        credential = await navigator.credentials.create({ publicKey: browserOpts });
      } catch (browserErr) {
        // Errores específicos del navegador → mensajes claros
        const name = browserErr?.name || "";
        const msg = browserErr?.message || "";
        if (name === "NotAllowedError") {
          toast.error("Has cancelado el registro o tu dispositivo no permite passkeys. Asegúrate de tener huella/PIN configurado.");
        } else if (name === "InvalidStateError") {
          toast.error("Esta passkey ya estaba registrada en este dispositivo");
        } else if (name === "NotSupportedError") {
          toast.error("Tu dispositivo no soporta passkeys. Requiere Android 7+ con bloqueo de pantalla activo o iOS 16+.");
        } else if (name === "SecurityError") {
          toast.error("Error de seguridad: el dominio no permite passkeys. Contacta con soporte.");
        } else if (name === "AbortError") {
          toast.error("Tiempo agotado. Vuelve a intentarlo.");
        } else {
          toast.error(`Passkey · ${name}: ${msg.slice(0, 200)}`);
        }
        return;
      }
      if (!credential) { toast.error("No se pudo crear la passkey"); return; }
      const payload = attestationToJson(credential);
      await api.post("/webauthn/register/verify", { credential: payload, device_name: deviceName });
      toast.success("Passkey registrada");
      setDeviceName("");
      load();
    } catch (e) {
      const backendDetail = formatErr(e?.response?.data?.detail);
      if (backendDetail && backendDetail !== "Algo salió mal. Inténtalo de nuevo.") toast.error(backendDetail);
      else toast.error(`Error: ${e?.name || ""} · ${e?.message || "desconocido"}`);
    } finally { setBusy(false); }
  };

  const revoke = async (cred) => {
    if (!window.confirm(`¿Revocar passkey "${cred.device_name}"?`)) return;
    try {
      await api.delete(`/webauthn/credentials/${cred.id}`);
      toast.success("Passkey revocada");
      load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  return (
    <div className="ag-card p-6" data-testid="passkeys-card">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold">Sprint G</span>
        <Label className="text-xs uppercase tracking-wider flex items-center gap-1.5">
          <Fingerprint weight="duotone" /> Inicio de sesión biométrico (Passkeys)
        </Label>
      </div>
      <p className="text-[11px] text-[var(--ag-text-dim)] mb-3">
        Registra Face ID, Touch ID o Windows Hello para iniciar sesión sin contraseña.
        {!supported && <span className="text-amber-600 ml-1">· Tu navegador no soporta passkeys.</span>}
      </p>

      {creds.length > 0 ? (
        <div className="space-y-2 mb-4">
          {creds.map((c) => (
            <div key={c.id} className="flex items-center justify-between bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded-sm p-3"
                 data-testid={`passkey-${c.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full bg-emerald-500/15 grid place-items-center shrink-0">
                  <ShieldCheck size={14} weight="fill" className="text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm text-[var(--ag-text)] truncate">{c.device_name || "Dispositivo"}</div>
                  <div className="text-[10px] text-[var(--ag-text-dim)]">
                    Creada {new Date(c.created_at).toLocaleDateString("es-ES")}
                    {c.last_used_at && ` · Último uso ${new Date(c.last_used_at).toLocaleDateString("es-ES")}`}
                  </div>
                </div>
              </div>
              <button onClick={() => revoke(c)} className="text-red-400 hover:text-red-300 p-1.5" data-testid={`passkey-revoke-${c.id}`}>
                <Trash size={14} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[11px] text-[var(--ag-text-dim)] italic mb-3">No tienes passkeys registradas.</div>
      )}

      <div className="flex gap-2 items-end">
        <div className="flex-1 max-w-xs">
          <Label className="text-[10px] uppercase tracking-wider">Nombre del dispositivo</Label>
          <Input value={deviceName} onChange={(e) => setDeviceName(e.target.value)}
            placeholder="Ej. iPhone, MacBook personal…" className="mt-1 rounded-sm"
            data-testid="passkey-device-name" />
        </div>
        <Button onClick={register} disabled={busy || !supported}
                className="bg-[var(--ag-gold)] hover:bg-[var(--ag-gold)]/90 text-[var(--ag-deep-blue)] rounded-sm"
                data-testid="passkey-register-btn">
          <Plus className="mr-1.5" weight="bold" /> {busy ? "Activando…" : "Registrar passkey"}
        </Button>
      </div>
    </div>
  );
}
